import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../AuthContext';
import './Login.css';
import icon from '../assets/dimassi_logo.svg'; 
import bg1 from '../assets/login6.avif';
import bg2 from '../assets/login7.avif';
import bg3 from '../assets/login8.avif';
import { Eye, EyeOff } from 'lucide-react';

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const slides = [
  {
    title: "Inteligencia",
    subtitle: " de Negocios.",
    description: "Gestión de activos y modelado de ventas en una plataforma centralizada.",
    image: bg1
  },
  {
    title: "Analítica",
    subtitle: " Predictiva.",
    description: "Visualiza tendencias y anticipa el comportamiento del mercado con datos precisos.",
    image: bg2
  },
  {
    title: "Control",
    subtitle: " Comercial.",
    description: "Seguimiento en tiempo real de inventarios y cumplimiento de metas.",
    image: bg3
  }
];

const Login = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [userName, setUserName] = useState('');
  const navigate = useNavigate();
const { verifyUser, login, session } = useAuth();

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (isLoginSuccess || isMobile) return;
    const timer = setInterval(() => setCurrentSlide((s) => (s + 1) % slides.length), 12000);
    return () => clearInterval(timer);
  }, [isLoginSuccess]);

  // Nuevo useEffect para asegurar que bg1 se muestre al hacer login
  useEffect(() => {
    if (isLoginSuccess) {
      setCurrentSlide(0); // Establece la primera imagen (bg1)
    }
  }, [isLoginSuccess]);


  useEffect(() => {
    if (session) navigate('/home');
  }, [session, navigate]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // La lógica de verificación ahora está centralizada en el contexto
      const { user, error } = await verifyUser(identifier, password);

      if (user) {
        setUserName(user.nombre || identifier.split('@')[0]);
        setIsLoginSuccess(true);
        // No reseteamos loading aquí para evitar parpadeo del botón durante la salida
        
        setTimeout(() => {
          setIsExiting(true);
        }, 2400);

        setTimeout(() => {
          login(user);
        }, 2400); 

      } else {
        setError(error.message || 'Credenciales no autorizadas');
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setError('Error de conexión o del sistema.');
      setLoading(false);
    }
  };

  // Variantes para la animación de los dividers
  const lineVariant = {
    hidden: { width: 0, opacity: 0 },
    visible: { 
      width: "100%", 
      opacity: 1,
      transition: { duration: 1, ease: "easeInOut", delay: 0.5 }
    }
  };

  return (
    <div className="login-container modern">
      {/* Carrusel de Fondo Dinámico (Cubre todo el contenedor) */}
      {!isMobile && (
        <AnimatePresence>
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={isExiting ? { opacity: 0 } : { opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.8 }}
            className="main-carousel-bg"
            style={{ backgroundImage: `url(${slides[currentSlide].image})` }}
          />
        </AnimatePresence>
      )}
      {!isMobile && <div className="main-visual-overlay" />}

      <motion.div 
        layout
        className="login-visual-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={isMobile ? { duration: 2.8 } : { duration: 2.5, ease: "easeInOut" }}
      >
        <div className={`brand-overlay ${isLoginSuccess ? 'center-content' : ''}`}>
          <motion.img 
            layout
            src={icon} 
            alt="Logo" 
            className="top-logo" 
            initial={{ opacity: 0, y: -20, scale: 1 }}
            animate={ // Adjusted transition duration for mobile
              isExiting 
                ? { opacity: 0, scale: 2.5 } 
                : isLoginSuccess ? { scale: 2.2, y: 0 } : { opacity: 1, y: 0, scale: 1 }
            }
            transition={{ duration: isExiting ? 2.6 : 2.8, ease: "easeInOut" }}
          />
          {!isLoginSuccess ? (
            <div className="hero-text">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentSlide}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="status-tag">SISTEMA ACTIVO v2.4</div>
                  <h1>{slides[currentSlide].title} <br/><span>{slides[currentSlide].subtitle}</span></h1>
                  <p>{slides[currentSlide].description}</p>
                </motion.div>
              </AnimatePresence>
              
              <div className="carousel-indicators">
                {slides.map((_, idx) => (
                  <div key={idx} className={`dot ${currentSlide === idx ? 'active' : ''}`} />
                ))}
              </div>
            </div>
          ) : (
            <motion.div 
              className="welcome-message"
              initial={{ opacity: 0, y: 20 }}
              animate={isExiting ? { opacity: 0, y: -20 } : { opacity: 1, y: 0 }}
              transition={isExiting ? { duration: 4.3 } : { delay: 0.1, duration: 4.5 }}
            >
              <h1>Bienvenido</h1>
              <p>{userName}</p>
            </motion.div>
          )}
        </div>
      </motion.div>

      <AnimatePresence>
      {!isLoginSuccess && (
      <motion.div 
        className="login-form-section"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 200, width: 0, padding: 0, transition: { duration: 0.6, ease: "easeOut" } }}
        transition={{ duration: 0.4 }}
      >
        <motion.div 
          className="form-wrapper"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, x: 100, transition: { duration: 0.8 } }}
          transition={{ duration: 0.8 }}
        >
          <motion.div className="animated-divider" variants={lineVariant} initial="hidden" animate="visible" />

          <div className="form-header">
            <span className="security-badge">SISTEMA ENCRIPTADO</span>
            <h2>Autenticación</h2>
            <p>Ingrese sus credenciales de consultor para acceder al panel.</p>
          </div>

          <form onSubmit={handleLoginSubmit}>
            {error && <motion.p initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="error-message">{error}</motion.p>}

            <div className="input-group">
              <label>ID CORPORATIVO</label>
              <input 
                type="text" 
                placeholder="Email o Usuario"
                value={identifier} 
                onChange={(e) => setIdentifier(e.target.value)} 
                required 
              />
            </div>

            <div className="input-group">
              <label>CLAVE DE ACCESO</label>
              <div className="input-field-wrapper">
                <input 
                  type={showPassword ? "text" : "password"} 
                  placeholder="••••••••"
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                />
                <button
                  type="button"
                  className="password-toggle-icon"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button type="submit" className="submit-button" disabled={loading}>
              {loading ? 'Iniciando...' : 'ENTRAR AL SISTEMA'}
              <span className="arrow">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </span>
            </button>
          </form>

          <motion.div className="animated-divider bot" variants={lineVariant} initial="hidden" animate="visible" transition={isMobile ? { duration: 0.5 } : { duration: 1.0 }} />
          
          <div className="form-footer">
            <p>© 2026 DIMASSI Group. Todos los derechos reservados.</p>
          </div>
        </motion.div>
      </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
};

export default Login;